import { useEffect, useState } from 'react';
import apiService from '../services/apiService';
import logo from '../assets/images/logo.png';

interface MaintenanceInfo {
  isEnabled: boolean;
  message?: string;
  estimatedEndTime?: string;
}

export default function MaintenancePage({ onResolved }: { onResolved: () => void }) {
  const [info, setInfo] = useState<MaintenanceInfo | null>(null);

  useEffect(() => {
    apiService.checkMaintenance().then(setInfo);
  }, []);

  // Re-check every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const data = await apiService.checkMaintenance();
      if (!data.isEnabled) {
        onResolved();
      }
      setInfo(data);
    }, 30000);
    return () => clearInterval(interval);
  }, [onResolved]);

  const endTime = info?.estimatedEndTime
    ? new Date(info.estimatedEndTime).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="min-h-screen bg-[#0F1114] flex flex-col px-6">
      {/* CricClash brand header — keeps the page recognisable as
          our app even though routes/main layout aren't mounted. */}
      <header className="pt-6">
        <div className="flex items-center gap-2">
          <img src={logo} alt="CricClash" className="w-10 h-10 rounded-lg object-cover" />
          <span className="text-xl font-bold text-white">CricClash</span>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="max-w-md space-y-6">
          {/* Icon */}
          <div className="text-6xl">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#13EC5B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-20 h-20 mx-auto opacity-80">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
          </div>

          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Under Maintenance</h1>
            <p className="text-[#9CA3AF] text-sm leading-relaxed">
              {info?.message || 'We are currently performing scheduled maintenance. Please check back shortly.'}
            </p>
          </div>

          {/* Estimated time */}
          {endTime && (
            <div className="bg-[#1A1D23] border border-[#2E3340] rounded-xl px-4 py-3">
              <p className="text-xs text-[#6B7280] mb-1">Estimated return</p>
              <p className="text-sm font-semibold text-white">{endTime}</p>
            </div>
          )}

          {/* Auto-refresh note */}
          <p className="text-xs text-[#4B5563]">
            This page will automatically refresh when maintenance is complete.
          </p>
        </div>
      </main>

      <footer className="py-6">
        <p className="text-xs text-[#4B5563] text-center">
          &copy; {new Date().getFullYear()} CricClash. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
